import React, { useState, useEffect } from 'react';
import Step1 from './components/Step1';
import Step2 from './components/Step2';
import Step3 from './components/Step3';
import PreviewModal from './components/PreviewModal';
import Toast from './components/Toast';
import MessageBanner from './components/MessageBanner';
// TRIAL LIMIT DISABLED: users can create unlimited websites.
// To re-enable, uncomment every block marked "TRIAL LIMIT DISABLED" in this file
// and in backend/create_website_lambda.py.
// import TrialCounter from './components/TrialCounter';
import './App.css';

function App() {
  const [formData, setFormData] = useState({
    websiteName: '',
    aboutUs: '',
    mainContent: '',
    links: '',
    contactUs: '',
    selectedTheme: '',
    darkMode: false,
    customColor: false,
    seoListing: false,
    email: '',
    phone: ''
  });

  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [themes, setThemes] = useState([]);
  const [toast, setToast] = useState(null);
  const [banner, setBanner] = useState(null);
  
  // Template-specific HTML cache
  const [templateHtmlCache, setTemplateHtmlCache] = useState({});
  const [step1Hash, setStep1Hash] = useState(null);
  
  // TRIAL LIMIT DISABLED: uncomment to re-enable
  // const [trialLimitReached, setTrialLimitReached] = useState(false);

  // API Gateway endpoints
  const PREVIEW_API_ENDPOINT = process.env.REACT_APP_PREVIEW_WEBSITE_API;
  const SUBMIT_API_ENDPOINT  = process.env.REACT_APP_CREATE_WEBSITE_API;

  useEffect(() => {
    const initialHash = getStep1Hash();
    setStep1Hash(initialHash);
  }, []);

  const getStep1Hash = () => {
    const savedData = localStorage.getItem('websiteFormData');
    if (!savedData) return null;
    
    try {
      const data = JSON.parse(savedData);
      return JSON.stringify({
        websiteName: data.websiteName,
        aboutUs: data.aboutUs,
        mainContent: data.mainContent,
        links: data.links,
        contactUs: data.contactUs
      });
    } catch (error) {
      console.error('Error parsing Step 1 data:', error);
      return null;
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleStep1Submit = () => {
    console.log('Step 1 submitted - Data is already saved in localStorage');
  };

  const handleStep1Change = () => {
    console.log('🔄 Step 1 data changed - clearing all cached HTML');
    setTemplateHtmlCache({});
    setStep1Hash(null); // Will be recalculated on next preview
  };

  // TRIAL LIMIT DISABLED: uncomment to re-enable
  // const handleTrialLimitReached = (limitReached) => {
  //   setTrialLimitReached(limitReached);
  // };

  const handlePreview = async () => {
    if (!formData.selectedTheme) {
      showToast('error', 'Please select a theme first!');
      return;
    }

    const savedData = localStorage.getItem('websiteFormData');
    if (!savedData) {
      showToast('error', 'Please fill and submit Step 1 form first!');
      return;
    }

    let step1Data;
    try {
      step1Data = JSON.parse(savedData);
    } catch (error) {
      console.error('Error parsing localStorage data:', error);
      showToast('error', 'Error reading form data. Please fill Step 1 again.');
      return;
    }

    const selectedTheme = themes.find(theme => theme.id === formData.selectedTheme);
    if (!selectedTheme) {
      showToast('error', 'Invalid theme selected. Please select a theme from Step 2.');
      return;
    }

    // Check if Step 1 data changed
    const currentHash = getStep1Hash();
    if (currentHash !== step1Hash) {
      console.log('🔄 Step 1 data changed since last preview - clearing cache');
      setTemplateHtmlCache({});
      setStep1Hash(currentHash);
    }

    // Check if we have cached HTML for this template
    const cachedData = templateHtmlCache[formData.selectedTheme];
    if (cachedData) {
      console.log(`✅ Using cached HTML for template: ${formData.selectedTheme}`);
      setShowPreview(true);
      return;
    }

    // No cache - generate new HTML
    console.log(`🆕 Generating new HTML for template: ${formData.selectedTheme}`);
    console.log('- Step 1 Data:', step1Data);
    console.log('- Selected Theme:', selectedTheme);

    setPreviewLoading(true);

    try {
      console.log('Fetching template HTML from:', selectedTheme.preview_url);
      const htmlResponse = await fetch(selectedTheme.preview_url);
      
      if (!htmlResponse.ok) {
        throw new Error('Failed to fetch template HTML');
      }
      
      const templateHtml = await htmlResponse.text();
      console.log('Template HTML fetched successfully, length:', templateHtml.length);

      const payload = {
        websiteName: step1Data.websiteName,
        aboutUs: step1Data.aboutUs,
        mainContent: step1Data.mainContent,
        links: step1Data.links,
        contactUs: step1Data.contactUs,
        selectedTheme: formData.selectedTheme,
        themeName: selectedTheme.name,
        darkMode: formData.darkMode,
        customColor: formData.customColor,
        seoListing: formData.seoListing,
        templateHtml: templateHtml
      };

      console.log('Calling Preview API with payload...');
      
      const generated = await callPreviewApi(payload);
      
      setTemplateHtmlCache(prev => ({
        ...prev,
        [formData.selectedTheme]: {
          generated: generated,
          edited: null
        }
      }));
      
      setShowPreview(true);
      
    } catch (error) {
      console.error('Error in preview:', error);
      showToast('error', `Failed to generate preview: ${error.message}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  const callPreviewApi = async (payload) => {
    try {
      console.log('Calling Preview API:', PREVIEW_API_ENDPOINT);

      const response = await fetch(PREVIEW_API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const responseData = await response.json();
      console.log('API Response:', responseData);

      if (response.ok && responseData.success) {
        showToast('success', responseData.message || 'Preview generated successfully!');
        return responseData.html;
      } else {
        const errorMessage = responseData.message || responseData.error || 'Failed to generate preview';
        throw new Error(errorMessage);
      }

    } catch (error) {
      console.error('API call failed:', error);
      throw error;
    }
  };

  const handleHtmlEdit = (editedHtml) => {
    console.log(`💾 Saving edited HTML for template: ${formData.selectedTheme}`);
    setTemplateHtmlCache(prev => ({
      ...prev,
      [formData.selectedTheme]: {
        ...prev[formData.selectedTheme],
        edited: editedHtml
      }
    }));
    showToast('success', 'HTML changes saved! Preview updated.');
  };

  const getCurrentHtml = () => {
    const cachedData = templateHtmlCache[formData.selectedTheme];
    if (!cachedData) return null;
    return cachedData.edited || cachedData.generated;
  };

  const handleStep3Submit = async () => {
    // ✅ Basic validation
    if (!formData.email || !formData.phone || !formData.selectedTheme) {
      showToast('error', 'Please fill all the details from Step 3!');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showToast('error', 'Please enter a valid email address!');
      return;
    }

    // ✅ Get Step 1 data
    const savedData = localStorage.getItem('websiteFormData');
    if (!savedData) {
      showToast('error', 'Step 1 data not found. Please fill and Submit Step 1 data first!');
      return;
    }

    let step1Data;
    try {
      step1Data = JSON.parse(savedData);
    } catch (error) {
      console.error('Error parsing localStorage data:', error);
      showToast('error', 'Error reading form data. Please fill Step 1 again.');
      return;
    }

    // ✅ Get selected theme
    const selectedTheme = themes.find(theme => theme.id === formData.selectedTheme);
    if (!selectedTheme) {
      showToast('error', 'Invalid theme selected. Please select a theme from Step 2.');
      return;
    }

    // ✅ CHECK: Do we have cached HTML from preview?
    const templateData = templateHtmlCache[formData.selectedTheme];
    let finalHtml = null;
    let skipAiGeneration = false;

    if (templateData && (templateData.edited || templateData.generated)) {
      // User previewed - use cached HTML
      finalHtml = templateData.edited || templateData.generated;
      skipAiGeneration = true;
      console.log(`✅ Using ${templateData.edited ? 'EDITED' : 'PREVIEWED'} HTML from cache`);
    } else {
      // User did NOT preview - will generate via AI on backend
      console.log('⚠️ No preview found - will generate HTML via AI on backend');
      skipAiGeneration = false;
    }

    setSubmitLoading(true);

    try {
      // Fetch template HTML
      console.log('Fetching template HTML from:', selectedTheme.preview_url);
      const htmlResponse = await fetch(selectedTheme.preview_url);
      
      if (!htmlResponse.ok) {
        throw new Error('Failed to fetch template HTML');
      }
      
      const templateHtml = await htmlResponse.text();

      // Build payload
      const payload = {
        website_name: step1Data.websiteName,
        about_us: step1Data.aboutUs,
        main_content: step1Data.mainContent.split(',').map(item => item.trim()).filter(item => item),
        links: step1Data.links.split(',').map(link => link.trim()).filter(link => link),
        // contact: {
        //   email: formData.email,
        //   phone: formData.phone,
        //   address: step1Data.contactUs || ''
        // },
        contactUs: step1Data.contactUs,
        email: formData.email,
        template_choice: formData.selectedTheme,
        theme_choice: formData.selectedTheme,
        theme: {
          dark_mode: formData.darkMode,
          custom_color: formData.customColor,
          seo_listing: formData.seoListing
        },
        templateHtml: templateHtml,
        finalHtml: finalHtml,  // null if not previewed, HTML if previewed
        skipAiGeneration: skipAiGeneration,  // true if previewed, false if not
        use_ai: !skipAiGeneration  // true if not previewed, false if previewed
      };

      console.log('📤 Submitting website creation:', {
        ...payload,
        finalHtml: finalHtml ? `${finalHtml.length} characters` : 'null (will use AI)',
        skipAiGeneration,
        use_ai: !skipAiGeneration
      });

      const response = await fetch(SUBMIT_API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      console.log('Create Website API Response:', result);

      if (response.ok && result.success) {
        const subdomainUrl = result.url;
        
        showToast('success', `🎉 Website created successfully!`);
        showBanner('success', null, subdomainUrl);
        console.log('✅ Website created:', {
          subdomain: result.subdomain,
          url: subdomainUrl,
          siteId: result.site_id,
          s3Key: result.s3_key
        });
        
        setTimeout(() => {
          const openSite = window.confirm(`Would you like to visit your new website?\n\n${subdomainUrl}`);
          if (openSite) {
            window.open(subdomainUrl, '_blank');
          }
        }, 2000);

        // TRIAL LIMIT DISABLED: uncomment to re-enable
        // if (window.refreshTrialCount) {
        //   setTimeout(() => {
        //     window.refreshTrialCount();
        //   }, 1000);
        // }
        
      } else {
        throw new Error(result.message || 'Failed to create website');
      }

    } catch (error) {
      console.error('Submission error:', error);
      showToast('error', `❌ Failed to create website`);
      showBanner('error', `Failed to create website: ${error.message}.`);
    } finally {
      setSubmitLoading(false);
    }
  };

  const showToast = (type, message) => {
    setToast({ type, message });
  };

  const closeToast = () => {
    setToast(null);
  };

  const showBanner = (type, message, url = null) => {
    setBanner({ type, message, url });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeBanner = () => {
    setBanner(null);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
  };

  const selectedThemeObj = themes.find(theme => theme.id === formData.selectedTheme);

  return (
    <div className="app-container">
      {/* Top Navbar */}
      <nav className="easyweb-navbar">
        <div className="easyweb-navbar-inner">
          <span className="easyweb-logo">EasyWeb</span>
        </div>
      </nav>

      

      {/* Hero Section */}
      <div className="easyweb-hero">
        <h1 className="easyweb-hero-title">
          Create your Website in 3 Easy Steps
        </h1>
        <p className="easyweb-hero-subtitle">
          Transform your ideas into a stunning website with our streamlined process
        </p>
        <div className="easyweb-steps-indicator">
          <div className="easyweb-step-dot step-1">1</div>
          <div className="easyweb-step-line"></div>
          <div className="easyweb-step-dot step-2">2</div>
          <div className="easyweb-step-line"></div>
          <div className="easyweb-step-dot step-3">3</div>
        </div>
      </div>

      {/* Banner - full width, below navbar */}
      {banner && (
        <MessageBanner 
          type={banner.type}
          message={banner.message}
          url={banner.url}
          onClose={closeBanner}
        />
      )}

      {/* <div className="container pb-5"> */}
      <div className="container pb-3" style={{ maxWidth: '1230px' }}>
        <div className="row gy-4" style={{ '--bs-gutter-x': '36px' }}>
          <div className="col-lg-4">
            <Step1 
              formData={formData} 
              onInputChange={handleInputChange} 
              onSubmit={handleStep1Submit}
              onStep1Change={handleStep1Change}
              showToast={showToast}
              // TRIAL LIMIT DISABLED: to re-enable, use disabled={trialLimitReached}
              // and uncomment the trialCounter prop below
              disabled={false}
              // trialCounter={
              //   <TrialCounter onLimitReached={handleTrialLimitReached} />
              // }
            />
          </div>
          <div className="col-lg-4">
            <Step2 
              formData={formData} 
              onInputChange={handleInputChange} 
              onPreview={handlePreview}
              previewLoading={previewLoading}
              onThemesLoaded={setThemes}
              // TRIAL LIMIT DISABLED: to re-enable, use disabled={trialLimitReached}
              disabled={false}
            />
          </div>
          <div className="col-lg-4">
            <Step3 
              formData={formData} 
              onInputChange={handleInputChange} 
              onSubmit={handleStep3Submit}
              themes={themes}
              submitLoading={submitLoading}
              // TRIAL LIMIT DISABLED: to re-enable, use disabled={trialLimitReached}
              disabled={false}
            />
          </div>
        </div>
      </div>{/* end .container */}

      {showPreview && selectedThemeObj && (
        <PreviewModal 
          theme={selectedThemeObj}
          templateId={formData.selectedTheme}
          templateHtmlCache={templateHtmlCache}
          onHtmlEdit={handleHtmlEdit}
          onClose={handleClosePreview}
        />
      )}

      {toast && (
        <Toast 
          type={toast.type}
          message={toast.message}
          onClose={closeToast}
          duration={5000}
        />
      )}
    </div>
  );
}

export default App;